import { inject, Injectable } from '@angular/core';

import { User, UserObject } from '../../bayadic-shared-models/classes/user/User';
import { StorageManagerProvider } from '../storage-manager/storage-manager';
import { SocketManagerProvider } from '../socket-manager/socket-manager';
import { SignupResponse } from '../../bayadic-shared-models/item/signup-response';
import { SignupRequest } from '../../bayadic-shared-models/item/signup-request';
import { EmailLoginData, LoginMethod, LoginRequest, TelegramLoginData } from '../../bayadic-shared-models/item/login-request';
import { LoginResponse } from '../../bayadic-shared-models/item/login-response';
import { ACTIONS } from '../../bayadic-shared-models/enums/Enums';
import { TelegramWebappService } from '@zakarliuka/ng-telegram-webapp';
import { Router } from '@angular/router';
import { Location } from '@angular/common';
import { LoggerService } from '../../app/services/logger.service';
import { HelperService } from '../../bayadic-shared-models/helper-service';
import { GetResetPasswordRequest, ResetPasswordResponse } from '../../bayadic-shared-models/messages/get-new-messages-request';


@Injectable()
export class UserManagerProvider {
    theme: boolean = true;
    user: User;
    loggingIn: any;
    alreadLoggedIn = false;
    private readonly telegramService = inject(TelegramWebappService)
    private silentLoginPromise: Promise<boolean> | null = null;

    constructor(
        public storageManager: StorageManagerProvider,
        public router: Router,
        public location: Location,
        private logger: LoggerService,
        public socketManager: SocketManagerProvider) {
        this.user = new User({} as UserObject);
        void this.captureInitialReturnUrl();
        this.init()
        this.silentLoginTelegram()
    }
    private isReturnUrlWorthSaving(url: string): boolean {
        if (!url || url === '/' || url === '/items') {
            return false;
        }
        return !url.startsWith('/profile/introduction')
            && !url.startsWith('/profile/login')
            && !url.startsWith('/profile/signup');
    }
    async rememberReturnUrl(url: string, options?: { force?: boolean }): Promise<void> {
        if (!this.isReturnUrlWorthSaving(url)) {
            return;
        }
        if (!options?.force) {
            const existing = await this.storageManager.get("url");
            if (existing && this.isReturnUrlWorthSaving(existing)) {
                return;
            }
        }
        await this.storageManager.set("url", url);
    }
    private async captureInitialReturnUrl(): Promise<void> {
        const url = window.location.pathname + window.location.search;
        await this.rememberReturnUrl(url, { force: true });
    }
    async navigateToReturnUrl(url: string): Promise<void> {
        const target = !url || url === '/' ? '/items' : url;
        await this.storageManager.set("url", null);
        await this.router.navigateByUrl(target);
    }
    async init() {
        await this.isLoggedIn()
        this.socketManager.accessToken = this.user.getMainAccessToken()
    }
    async silentLoginTelegram(): Promise<boolean> {
        if (this.silentLoginPromise) {
            return this.silentLoginPromise;
        }
        this.silentLoginPromise = this.performSilentLoginTelegram();
        try {
            return await this.silentLoginPromise;
        } finally {
            this.silentLoginPromise = null;
        }
    }
    private async performSilentLoginTelegram(): Promise<boolean> {
        if (this.alreadLoggedIn) {
            return true;
        }
        this.telegramService.ready();
        if (!this.telegramService.initData) {
            return false;
        }
        const connected = await this.ensureSocketConnected();
        if (!connected) {
            return false;
        }
        this.user = new User({} as UserObject);
        const userInitData = new URLSearchParams(this.telegramService.initData);
        const loginData: TelegramLoginData = {
            queryString: this.telegramService.initData,
            user: JSON.parse(userInitData.get("user")),
            username: userInitData.get("username"),
            hash: userInitData.get("hash"),
            authDate: userInitData.get("auth_date")
        };
        const userData = await this.requestUserData(loginData, LoginMethod.telegram);
        if (!userData || !userData.loggedIn) {
            return false;
        }
        await this.setupUser(userData);
        const url = await this.storageManager.get("url");
        if (url) {
            await this.navigateToReturnUrl(url);
        }
        return true;
    }
    private async ensureSocketConnected(timeoutMs = 10000): Promise<boolean> {
        await this.socketManager.connect();
        const start = Date.now();
        while (!this.socketManager.connected && Date.now() - start < timeoutMs) {
            await new Promise(resolve => setTimeout(resolve, 100));
        }
        return this.socketManager.connected;
    }
    async isLoggedIn() {
        if (this.alreadLoggedIn) {
            this.socketManager.loggedIn = true;
            this.socketManager.onLoggedIn()
            return true
        };

        const userData: UserObject = await this.storageManager.get("user")
        if (!userData) {
            await this.rememberReturnUrl(this.router.url);

            return false;
        }
        this.user = new User(userData);
        if (this.isTokenExpired(this.user.getMainAccessToken())) {
            await this.rememberReturnUrl(this.router.url);
            this.logger.debug("session has been expired", this.user.getMainAccessToken())
            return false;
        }
        this.alreadLoggedIn = true;
        return true;
    }
    decodeJWT(token: string): any | false {
        if (!token || token.length < 10 || token.indexOf(".") < 0) return false;
        const payloadBase64 = token.split('.')[1];
        const payload = JSON.parse(atob(payloadBase64));
        return payload;
    }
    isTokenExpired(token: string) {
        const payload = this.decodeJWT(token);
        if (!payload) return true;
        const expirationTimestamp = payload.exp * 1000; // Convert to milliseconds
        const currentTime = Date.now();

        return currentTime > expirationTimestamp;
    }
    isUser() {
        return this.isUserValid();
    }
    public async getUser(): Promise<User | false> {
        if (this.isUserValid())
            return this.user;
        else {
            const user = await this.getUserFromStorage()
            this.user = user;
            if (this.isUserValid()) {

                return this.user
            }
            else {
                return false
            }
        }
    }
    private isUserValid() {
        return this.user && this.user.hasAccessToken();
    }

    private getUserFromStorage(): Promise<User> {
        return new Promise((resolve) => {
            this.storageManager.get('user').then(user => {
                // return  new User(user);
                resolve(new User(user));
            })
        });
    }
    async logOut() {
        this.storageManager.set("user", null);
        this.alreadLoggedIn = false;
        this.socketManager.loggedIn = false;
    }
    async login(): Promise<boolean> {
        const loginData: EmailLoginData = { email: this.user.email, password: this.user.password };
        const userData = await this.requestUserData(loginData, LoginMethod.email);
        if (!userData || !userData.loggedIn) return false;
        await this.setupUser(userData);
        this.router.navigate(["items"]);

        return true;
    }
    private async setupUser(userData: LoginResponse) {
        this.user.userID = userData.userID;
        this.user.setMainAccessToken(userData.mainAccessToken);
        this.socketManager.accessToken = this.user.getMainAccessToken();
        this.socketManager.loggedIn = true;
        this.socketManager.onConnect();
        await this.save();
        this.alreadLoggedIn = true;
    }

    public async requestUserData(loginData : EmailLoginData | TelegramLoginData, loginMethod: LoginMethod) {
        const userData = await this.socketManager.asyncQuery<LoginResponse, LoginRequest>(ACTIONS.login, { user: loginData, loginMethode: loginMethod});
        return userData;
    }

    async isFirstUse() {
        return new Promise((resolve) => {
            this.storageManager.get("usedBefore").then(isUsed => {
                if (isUsed == true) {
                    resolve(true);
                }
                else resolve(false);
            });
        }
        );
    }
    async signUp() {
        const user = this.user;
        const signUpResult = await this.socketManager.asyncQuery<SignupResponse, SignupRequest>(ACTIONS.SignUp, { user: user })
        if (signUpResult.created) {
            this.setMainAccessToken(signUpResult.mainAccessToken);
            this.user.userID = signUpResult.userID;
            this.socketManager.accessToken = this.user.getMainAccessToken()
        }
        return signUpResult;
    }
    async verifyEmail(email: string) {

        if (!HelperService.validateEmail(email)) {
            return false;
        }
        const result = await this.socketManager.asyncQuery<ResetPasswordResponse, GetResetPasswordRequest>(ACTIONS.resetPassword, { email: email });
        if (result.isAllowed) {
            return true;
        }
        else {
            return false;
        }
    }   
    setMainAccessToken(mainAccessToken: string) {
        this.user.setMainAccessToken(mainAccessToken);
    }
    async save() {
        await this.storageManager.set('user', this.user);
    }
    async refreshUserData(): Promise<User | false> {
        if (!this.user || !this.user.userID) {
            return false;
        }
        try {
            const response = await this.socketManager.asyncQuery<GetUserDataResponse, any>("getUserData", { userID: this.user.userID });
            if (response && response.userData) {
                const updatedUser = new User(response.userData);
                // Preserve the access token
                updatedUser.setMainAccessToken(this.user.getMainAccessToken());
                this.user = updatedUser;
                await this.save();
                return this.user;
            }
            return false;
        } catch (error) {
            this.logger.debug('Error refreshing user data:', error);
            return false;
        }
    }
}
interface GetUserDataResponse {
    userData: any;
}
